import Backbone from 'backbone';
import _ from 'underscore';
import { Validator } from './validator';
import { Converter } from './converter';

/**
 * Viewmodel schema, generated from schema file. It provides auto generated
 * default value, validation rules and conversions
 * @param {Object} vmSchema - viewmodel-schema instance created from schema
 * @param {JsonSchema} schema - original entity schema
 * @param {Object} model - raw model with default values
 * @param {String} odataType - odatatype string used for odata calls
 * @returns {Object} - return default values merged from both inputs
 */
export function createBackboneModel(vmSchema, schema, model, odataType) {
  return Backbone.Model.extend({
    constructor: function constructor(...params) {
      this.validator = new Validator(vmSchema, this);
      this.errors = this.validator.errors;
      this.converter = new Converter(vmSchema, this);
      this.vmSchema = vmSchema;
      Backbone.Model.apply(this, params);
    },

    /**
    * get default value for backbone model.
    * this method is called internally by backbone to fetch initial value.
    * @param {Object} vmSchema - viewmodel-schema that contains default values
    * @param {Object} model - raw model that contains default values
    * @returns {Object} - return default values merged from both inputs
    */
    defaults() {
      return _.mapObject(vmSchema, (schemaField, name) => {
        if (schemaField.prop.type === 'object') {
          const childType = odataType ? `#Model.${schema.name}` : null;
          const NestedModel = createBackboneModel(_.omit(schemaField, 'prop'), schema, model, childType);

          return new NestedModel();
        }

        const modelValue = _.result(model, name, schemaField.default);
        const converted = schemaField.conversion.fromModel(modelValue);

        return _.isUndefined(converted) ? '' : converted;
      });
    },

    /**
    * validate field attributes for backbone model.
    * this method is called internally by backbone when isValid and save are called.
    * @param {Array} attrs - array of field attributes
    * @returns {Object} - error object if any, undefined if validation is successful
    */
    validate(attrs) {
      return this.validator.validate(attrs);
    },

    /**
    * parse values from raw model and fills backbone model.
    * this method is exposed as external api of backbone model.
    * @param {Object} response - the plain object to parse
    * @returns {undefined}
    */
    parse(response) {
      return this.converter.parse(response);
    },

    /**
    * convert backbone model to plain object.
    * this method is exposed as external api of backbone model.
    * @returns {Object} - plain object that contains converted field values
    */
    toJSON() {
      const json = this.converter.toJSON();

      if (odataType) {
        json['@odata.type'] = odataType;
      }

      return json;
    },
  });
}
